import React, { useEffect, useState } from "react";
import { CalendarDays, Check } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { CalendarExport } from "@/components/CalendarExport";

const INSTRUMENT_NAMES = { singers: "Singing", drummers: "Drums", guitarists: "Guitar", pianists: "Piano" };

const FOCUS = {
  singers: "vocal warm-up, scales in sol-fa and sight-singing",
  drummers: "rudiments on a pad, grooves with a metronome and reading drum charts",
  guitarists: "finger warm-up, scales in different positions and reading TAB with notation",
  pianists: "five-finger warm-up, scales with both hands and reading the grand staff",
};

const DAYS = (focus, rest) => [
  ["Sun", "New lesson", "Work through the next lesson and its play-along (20 – 30 mins)."],
  ["Mon", "Practice", `Warm up, then the lesson's practice steps: ${focus} (20 mins).`],
  ["Tue", "Theory", "Check-yourself quizzes and Music Terms flashcards (15 mins)."],
  ["Wed", "Play along", "Loop the tricky bars at a slower tempo, then speed up (20 mins)."],
  ["Thu", "Review", "Re-read the lesson's music without stopping and retake the knowledge check (15 mins)."],
  ["Fri", "Record", "Record a take of the play-along and send it to Jason (15 mins)."],
  ["Sat", "Rest", `Rest day — or a relaxed 10 minutes ${rest} something you enjoy.`],
];

const useSchedule = () => {
  const { user } = useAuth();
  const [s, setS] = useState(null);
  useEffect(() => { if (user) api.get("/schedule").then(({ data }) => setS(data)).catch(() => {}); }, [user]);
  const tick = async (day, done) => {
    try { const { data } = await api.post("/schedule/tick", { day, done }); setS(data); if (done) toast.success("Nice work — day ticked off"); }
    catch (e) { toast.error("Couldn't save that"); }
  };
  return [s, tick];
};

const DayTick = ({ s, i, tick }) => {
  if (!s) return null;
  const day = s.days[i];
  if (day > s.today) return <span className="text-[11px] text-muted-foreground" data-testid={`practice-tick-${i}`}>Coming up</span>;
  const on = s.ticked.includes(day);
  return (
    <button onClick={() => tick(day, !on)} role="checkbox" aria-checked={on} data-testid={`practice-tick-${i}`}
      className={`mt-2 inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs transition-colors ${on ? "border-primary bg-primary text-primary-foreground" : "border-border text-muted-foreground hover:border-primary hover:text-primary"}`}>
      <Check size={12} /> {on ? "Done" : day === s.today ? "Tick off today" : "Mark done"}
    </button>
  );
};

export const PracticeSchedule = ({ instrumentId, weeks }) => {
  const [s, tick] = useSchedule();
  const days = DAYS(FOCUS[instrumentId] || FOCUS.pianists, instrumentId === "singers" ? "singing" : "playing");
  return (
  <section className="mt-10 rounded-2xl border border-border bg-card p-6" data-testid="practice-schedule">
    <div className="flex items-center gap-2">
      <CalendarDays size={18} className="text-primary" />
      <h2 className="font-serif text-2xl">Suggested practice schedule</h2>
    </div>
    <p className="mt-1 text-sm text-muted-foreground" data-testid="practice-schedule-summary">
      {weeks ? `One lesson a week finishes the course in ${weeks} weeks. ` : ""}Little and often works best: 15–30 minutes a day, six days a week.
      {s && <span className="ml-1 font-medium text-primary" data-testid="practice-week-count">This week: {s.ticked.filter((d) => d < s.days[6]).length}/6 days done.</span>}
      {s?.practice_badges > 0 && <span className="ml-1 font-medium text-primary" data-testid="practice-badges">· {s.practice_badges} Practice Streak {s.practice_badges === 1 ? "badge" : "badges"}</span>}
    </p>
    <ol className="mt-5 grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
      {days.map(([d, t, txt], i) => (
        <li key={d} className="rounded-xl border border-border bg-background p-3" data-testid={`practice-day-${d.toLowerCase()}`}>
          <p className="font-mono text-[11px] uppercase tracking-widest text-primary">{d} · {t}</p>
          <p className="mt-1 text-sm text-foreground">{txt}</p>
          {i < 6 && <DayTick s={s} i={i} tick={tick} />}
        </li>
      ))}
    </ol>
    <CalendarExport days={days} course={`${INSTRUMENT_NAMES[instrumentId] || "Music"} practice`} />
    <p className="mt-4 text-xs text-muted-foreground">Busy week? Do Sunday's lesson and two practice days, and move on when you pass the knowledge check.{s ? " Miss a day and we'll send a friendly nudge (max twice a week, if Email reminders are on)." : ""}</p>
  </section>
  );
};
